import { ContextType, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from 'components/buttons/button';
import { ButtonType } from 'components/buttons/button-types';
import { Card } from 'components/cards/card';
import { CardThemeType } from 'components/cards/card-type';
import { FeaturePanel } from 'components/panels/feature-panel/feature-panel';
import { AppContext } from 'core/context/app.context';
import { FeatureFlag } from 'core/enums/feature-flag';
import { HaptikLaunchMessage } from 'core/enums/haptik-launch-messages';
import { Util } from 'utils/common.utils';
import { _noop } from 'utils/function.utils';
import styles from './notice.module.scss';

type NoticeButton = {
  name: string;
  title: string;
  type?: ButtonType;
  analyticsTitle?: string;
  analyticsTargetUrl?: string;
  analyticsProductName?: string;
};

type Brand = {
  logo?: string;
  name?: string;
};

type BrandItem = {
  logo?: string;
  name?: string;
  iconImage?: string;
  iconAlt?: string;
};

type Lang = {
  text: string;
  defaults?: string;
  date?: string;
  packageName?: string;
};

type BrandBadgesProps = {
  brands: BrandItem[];
};

const BrandBadges = ({ brands }: BrandBadgesProps) => {
  return (
    <div className={`${styles.brandBadgesContainer} cust-mb-3`}>
      {brands.map((brandItem, index) => {
        const brandImageSrc = brandItem.iconImage || brandItem.logo || '';
        const brandImageAlt = brandItem.iconAlt || (brandItem.name ? `${brandItem.name} logo` : 'brand logo');
        const hasBrandImage = brandItem.iconImage || brandItem.logo;
        const brandKey = brandItem.name || brandItem.logo || brandItem.iconImage || `brand-${index}`;

        return (
          <div key={brandKey} className={styles.brandBadge}>
            {hasBrandImage && <img src={brandImageSrc} alt={brandImageAlt} className={styles.brandBadgeIcon} />}

            {brandItem.name && <span className={styles.brandBadgeName}>{brandItem.name}</span>}
          </div>
        );
      })}
    </div>
  );
};

type BrandBadgeProps = {
  brand?: Brand;
  iconImage?: string;
  iconAlt?: string;
  brandAlt: string;
};

const BrandBadge = ({ brand, iconImage, iconAlt, brandAlt }: BrandBadgeProps) => {
  const brandImageSrc = iconImage || brand?.logo || '';
  const brandImageAlt = iconAlt || brandAlt;
  const hasBrandImage = brand?.logo || iconImage;

  return (
    <div className={`${styles.brandBadge} cust-mb-3`}>
      {hasBrandImage && <img src={brandImageSrc} alt={brandImageAlt} className={styles.brandBadgeIcon} />}

      {brand?.name && <span className={styles.brandBadgeName}>{brand.name}</span>}
    </div>
  );
};

type IconDisplayProps = {
  iconImage?: string;
  iconAlt: string;
  iconClass?: string;
  defaultIcon: string;
  largeIcon?: boolean;
  success?: boolean;
  info?: boolean;
};

const IconDisplay = ({ iconImage, iconAlt, iconClass, defaultIcon, largeIcon, success, info }: IconDisplayProps) => {
  const largeIconClass = largeIcon ? styles.topIconLarge : '';

  const statusClass = success
    ? styles.success
    : info
      ? styles.info
      : styles.warning;

  const resolvedIconClass = iconClass || defaultIcon;

  const standaloneIconClassName = `${styles.standaloneIcon} ${largeIconClass}`;
  const iconElementClassName = `${styles.topIcon} ${largeIconClass} ${resolvedIconClass} ${statusClass}`;

  if (iconImage) {
    return <img src={iconImage} alt={iconAlt} className={standaloneIconClassName} />;
  }

  return <i className={iconElementClassName} />;
};

type NoticeButtonsProps = {
  success?: boolean;
  info?: boolean;
  hideAllButtons?: boolean;
  hideButton?: boolean;
  additionalButtonAction?: () => void;
  additionalButton?: NoticeButton;
  button?: NoticeButton;
  appContext: ContextType<typeof AppContext> | null;
  analyticsAreaName: string;
  analyticsComponentName: string;
  analyticsAdditionalProps?: any;
  onChatToAgent: () => void;
  onButtonClick: () => void;
};

const NoticeButtons = (props: NoticeButtonsProps) => {
  const {
    success,
    info,
    hideAllButtons,
    hideButton,
    additionalButtonAction,
    additionalButton,
    button,
    appContext,
    analyticsAreaName,
    analyticsComponentName,
    analyticsAdditionalProps,
    onChatToAgent,
    onButtonClick,
  } = props;

  const { t } = useTranslation();

  if (hideAllButtons) {
    return null;
  }

  const showChatToAgent = !success && !info;
  const hasChatToAgentFeature = appContext?.featureStore.hasViewPermission(FeatureFlag.ErrorNoticeChatToAgent);

  const chatToAgentButtonMeta = {
    name: 'chatToAgent',
    title: t('buttons.chatToAgent'),
    areaName: analyticsAreaName,
    analyticsTitle: `${button?.analyticsTitle} ${t('buttons.chatToAgent')}`,
    analyticsTargetUrl: button?.analyticsTargetUrl,
    productName: button?.analyticsProductName,
    type: ButtonType.OutlineDark,
  };

  const additionalButtonMeta = additionalButton
    ? {
      name: additionalButton.name,
      title: t(additionalButton.title),
      type: additionalButton.type,
      areaName: analyticsAreaName,
      analyticsTitle: `${additionalButton.analyticsTitle} ${t(additionalButton.title)}`,
      analyticsTargetUrl: additionalButton.analyticsTargetUrl,
      productName: additionalButton.analyticsProductName,
    }
    : null;

  const mainButtonMeta = button
    ? {
      name: button.name,
      title: t(button.title),
      type: button.type,
      areaName: analyticsAreaName,
      analyticsTitle: `${button.analyticsTitle} ${t(button.title)}`,
      analyticsTargetUrl: button.analyticsTargetUrl,
      productName: button.analyticsProductName,
    }
    : null;

  return (
    <div className="d-flex justify-content-center cust-mt-4">
      {showChatToAgent && (
        <FeaturePanel hasFeature={hasChatToAgentFeature}>
          <Button meta={chatToAgentButtonMeta} classes={['cust-ml-1']} onClick={onChatToAgent} />
        </FeaturePanel>
      )}

      {additionalButtonAction && additionalButtonMeta && (
        <Button
          meta={additionalButtonMeta}
          classes={['cust-ml-1']}
          onClick={additionalButtonAction}
          analyticsComponentName={analyticsComponentName}
          analyticsAdditionalProps={analyticsAdditionalProps}
        />
      )}

      {!hideButton && mainButtonMeta && (
        <Button
          meta={mainButtonMeta}
          classes={['cust-ml-1']}
          onClick={onButtonClick}
          analyticsComponentName={analyticsComponentName}
          analyticsAdditionalProps={analyticsAdditionalProps}
        />
      )}
    </div>
  );
};

type NoticeProps = {
  icon?: string;
  heading?: string;
  text?: string;
  additionalText?: JSX.Element | string | null;
  footerText?: JSX.Element | string | null;
  date?: string;
  lang?: Lang;
  button?: NoticeButton;
  additionalButton?: NoticeButton;
  success?: boolean;
  info?: boolean;
  onSurveyClick?: () => void;
  padding?: boolean;
  hideButton?: boolean;
  additionalButtonAction?: () => void;
  hideAllButtons?: boolean;
  onComplete?: () => void;
  closeOverlay?: () => void;
  analyticsComponentName?: string;
  analyticsAreaName?: string;
  analyticsAdditionalProps?: any;
  brand?: Brand;
  brands?: BrandItem[];
  iconClass?: string;
  largeIcon?: boolean;
  hideBrand?: boolean;
  iconImage?: string;
  iconAlt?: string;
};

export const Notice = (props: NoticeProps) => {
  const {
    heading,
    text,
    lang,
    additionalText,
    footerText,
    additionalButton = { name: 'Back', title: 'buttons.back', type: ButtonType.OutlineDark },
    button = { name: 'Done', title: 'buttons.done', type: ButtonType.Dark },
    success,
    info,
    onSurveyClick,
    padding,
    hideButton,
    additionalButtonAction,
    hideAllButtons,
    onComplete,
    closeOverlay = _noop,
    analyticsComponentName = 'button',
    analyticsAdditionalProps,
    analyticsAreaName = 'notice',
    brand,
    brands,
    iconClass,
    largeIcon,
    hideBrand,
    iconImage,
    iconAlt,
  } = props;

  const appContext = useContext(AppContext);

  const handleButtonClick = () => {
    if (onComplete) {
      onComplete();
    } else {
      closeOverlay();
    }
  };

  const successInfoOrDefaultValue = (successValue: string, infoValue: string, defaultValue: string) => {
    return success
      ? successValue
      : info
        ? infoValue
        : defaultValue
  }

  const handleChatToAgent = () => {
    appContext?.haptikStore.showApp(HaptikLaunchMessage.HaptikChatToAgent);
    closeOverlay();
  };

  const { t } = useTranslation();

  const hasMultipleBrands = brands && brands.length > 1;
  const hasSingleBrand = brand && (!!brand?.name || !!brand?.logo);
  const showBranding = !hideBrand && (hasSingleBrand || hasMultipleBrands || !!iconImage);

  const brandAlt = brand?.name ? `${brand.name} logo` : 'brand logo';

  const defaultIcon = 'icon-' + successInfoOrDefaultValue('wk-dstv-tick-in-circle', 'info', 'cancel-policy');

  const defaultIconImage =
    success || iconClass
      ? undefined
      : info
        ? Util.GetImageAsset('error-handling/info.png')
        : Util.GetImageAsset('error-handling/error-icon.png');

  const resolvedIconImage = iconImage || defaultIconImage;

  const resolvedIconAlt = iconAlt
    ? iconAlt
    : successInfoOrDefaultValue('success', 'info', 'error') + ' icon';

  const cardClasses = ['no-margin', padding ? '' : 'px-0 pb-0'].filter(Boolean);

  const containerPaddingClass = padding ? 'cust-px-4 cust-pt-4' : '';
  const containerClassName = `text-center ${containerPaddingClass} ${styles.noticeCardContainer}`;

  let textMarginClass = '';
  if (success || info) {
    textMarginClass = additionalText ? 'cust-mb-2' : 'cust-mb-4';
  }

  const textClassName = `text-regular no-margin ${textMarginClass}`;

  const surveyButtonClassName = `text-regular cust-mt-2 btn ${styles.surveyLink}`;

  const renderBrandingOrIcon = () => {
    if (showBranding) {
      if (hasMultipleBrands && brands) {
        return <BrandBadges brands={brands} />;
      }

      return <BrandBadge brand={brand} iconImage={resolvedIconImage} iconAlt={resolvedIconAlt} brandAlt={brandAlt} />;
    }

    return (
      <IconDisplay
        iconImage={resolvedIconImage}
        iconAlt={resolvedIconAlt}
        iconClass={iconClass}
        defaultIcon={defaultIcon}
        largeIcon={largeIcon}
        success={success}
        info={info}
      />
    );
  };

  const transValues = lang ? { disconnectDate: lang.date, packageName: lang.packageName } : undefined;
  const transComponents = { break: <br />, bold: <b></b> };

  return (
    <Card classes={cardClasses} backgroundType={CardThemeType.Light} allowOverflow={true} mobilePadding={false}>
      <div className={containerClassName}>
        {renderBrandingOrIcon()}

        {heading && <h4 className="cust-mb-3 cust-pt-1 text-large">{heading}</h4>}

        {lang && (
          <div className="text-regular text-center cust-mb-4">
            <Trans i18nKey={lang.text} defaults={lang.defaults} values={transValues} components={transComponents} />
          </div>
        )}

        {text && (
          <p className={textClassName} style={{ whiteSpace: 'pre-line' }}>
            {text}
          </p>
        )}

        {additionalText && <div className="text-regular cust-mt-1">{additionalText}</div>}

        {onSurveyClick && (
          <button className={surveyButtonClassName} onClick={onSurveyClick}>
            {t('actionOverlays.linkPackage.activationSurvey')}
          </button>
        )}

        <NoticeButtons
          success={success}
          info={info}
          hideAllButtons={hideAllButtons}
          hideButton={hideButton}
          additionalButtonAction={additionalButtonAction}
          additionalButton={additionalButton}
          button={button}
          appContext={appContext}
          analyticsAreaName={analyticsAreaName}
          analyticsComponentName={analyticsComponentName}
          analyticsAdditionalProps={analyticsAdditionalProps}
          onChatToAgent={handleChatToAgent}
          onButtonClick={handleButtonClick}
        />

        {footerText && <div className="text-small cust-mt-4">{footerText}</div>}
      </div>
    </Card>
  );
};